import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { HeroScene } from "./HeroScene";
import { MaskedLine } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { scrollY } = useScroll();
  const contentY = useTransform(scrollY, [0, 640], [0, 130]);
  const contentOpacity = useTransform(scrollY, [0, 520], [1, 0.15]);

  return (
  <section
    id="top"
    data-testid="hero-section"
    className="relative flex min-h-screen flex-col justify-center overflow-hidden"
  >
    <div className="absolute inset-0 grid-bg" />
    <HeroScene />
    <div className="pointer-events-none absolute -left-40 top-24 h-[480px] w-[480px] rounded-full bg-blue-600/20 blur-[140px]" />
    <div className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-cyan-400/10 blur-[130px]" />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-[#020617]" />

    <motion.div
      style={{ y: contentY, opacity: contentOpacity }}
      className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pt-28 md:px-12"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
        className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2 backdrop-blur-md"
        data-testid="hero-badge"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse-dot" />
        <span className="font-mono-tech text-[0.65rem] uppercase tracking-[0.3em] text-slate-300">
          Veerox Software · Built for Success
        </span>
      </motion.div>

      <h1 className="mt-8 max-w-5xl font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-8xl">
        <MaskedLine delay={0.3}>One Vision.</MaskedLine>
        <MaskedLine delay={0.45}>Six Ventures.</MaskedLine>
        <MaskedLine delay={0.6} className="text-gradient-cyan pb-2">
          Infinite Momentum.
        </MaskedLine>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
        className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
        data-testid="hero-subtitle"
      >
        Veerox Group is the force behind an ecosystem of ventures — from AI
        workforces and autonomous trading intelligence to enterprise SaaS and
        premium food brands. We build companies that build the future.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <a
          href="#ventures"
          data-testid="hero-cta-ventures"
          className="btn-primary group inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-base font-semibold text-white"
        >
          Explore Our Ventures
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
        <a
          href="#contact"
          data-testid="hero-cta-contact"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-8 py-4 font-display text-base font-semibold text-white backdrop-blur-md transition-colors duration-300 hover:bg-white/[0.08]"
        >
          Get In Touch
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="mt-14 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono-tech text-[0.65rem] uppercase tracking-[0.22em] text-slate-500"
        data-testid="hero-pillars"
      >
        <span>AI Workforce</span>
        <span className="text-cyan-400">•</span>
        <span>Trading Intelligence</span>
        <span className="text-cyan-400">•</span>
        <span>Enterprise SaaS</span>
        <span className="text-cyan-400">•</span>
        <span>Food Brands</span>
      </motion.div>
    </motion.div>

    <motion.a
      href="#marquee"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.8, duration: 1 }}
      className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-slate-500"
      data-testid="hero-scroll-indicator"
      aria-label="Scroll down"
    >
      <ChevronDown className="h-6 w-6 animate-bounce" />
    </motion.a>
  </section>
  );
};
